import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Plane, Users, Gauge, Battery, Trophy, RefreshCw, Clock, MapPin, AlertTriangle, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WeeklyReportPage() {
  const { token, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/projects`, { headers });
      setProjects(r.data || []);
      if (r.data?.[0] && !selectedProject) setSelectedProject(r.data[0].id);
    } catch { /* noop */ }
  }, [selectedProject]);

  const loadReport = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/weekly/current?project_id=${pid}`, { headers });
      setReport(r.data);
    } catch (e) {
      toast.error('Falha ao carregar relatorio');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { if (selectedProject) loadReport(selectedProject); }, [selectedProject, loadReport]);

  const handleRegenerateAll = async () => {
    if (user?.role !== 'super_admin') return;
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/api/reports/weekly/generate`, {}, { headers });
      toast.success(`${r.data.generated} relatorios gerados`);
      if (selectedProject) loadReport(selectedProject);
    } catch {
      toast.error('Falha ao gerar relatorios');
    } finally {
      setRegenerating(false);
    }
  };

  const exportJson = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_semanal_${report.project_name || report.project_id}_${report.week_key}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = report?.totals || {};
  const rec = report?.records || {};
  const daily = (report?.daily || []).map((d) => ({ ...d, km: Number((d.distance_m / 1000).toFixed(2)) }));

  return (
    <div className="space-y-6" data-testid="weekly-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <BarChart3 size={20} className="text-gold" /> RELATORIO SEMANAL
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200">
            AUTO SEG 08:00
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[220px] h-9 text-sm" data-testid="wr-project-select">
              <SelectValue placeholder="Selecione projeto" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => loadReport(selectedProject)} disabled={loading} data-testid="wr-reload">
            <RefreshCw size={14} className={loading ? 'animate-spin mr-1' : 'mr-1'} /> ATUALIZAR
          </Button>
          {user?.role === 'super_admin' && (
            <Button variant="outline" size="sm" onClick={handleRegenerateAll} disabled={regenerating} data-testid="wr-regenerate-all">
              {regenerating ? 'GERANDO...' : 'GERAR TUDO'}
            </Button>
          )}
        </div>
      </div>

      {!report && !loading && (
        <Card className="card-hud p-10 text-center text-gray-400">Selecione um projeto para ver o relatorio</Card>
      )}

      {loading && <Card className="card-hud p-10 text-center text-gray-400">Carregando...</Card>}

      {report && (
        <>
          {/* Period banner */}
          <Card className="card-hud p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-purple-600">PERIODO</p>
                <p className="text-navy text-sm font-semibold">
                  {report.period_start_utc?.slice(0,10)} a {report.period_end_utc?.slice(0,10)} · {report.week_key}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={exportJson} data-testid="wr-export">
                <Download size={14} className="mr-1" /> EXPORTAR JSON
              </Button>
            </div>
          </Card>

          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Plane} label="Voos" value={t.flights || 0} color="text-blue-600 bg-blue-50" testid="wr-stat-flights" />
            <StatCard icon={MapPin} label="Distancia" value={`${t.distance_km || 0} km`} color="text-purple-600 bg-purple-50" testid="wr-stat-km" />
            <StatCard icon={Clock} label="Tempo total" value={t.duration_hhmm || '0h00'} color="text-emerald-600 bg-emerald-50" testid="wr-stat-time" />
            <StatCard icon={Users} label="Pilotos ativos" value={t.pilots_active || 0} color="text-amber-600 bg-amber-50" testid="wr-stat-pilots" />
          </div>

          {/* Daily chart */}
          {daily.length > 0 && (
            <Card className="card-hud p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3">VOOS POR DIA</p>
              <div style={{ height: 200 }} data-testid="wr-daily-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} name="Voos" />
                    <Bar dataKey="km" fill="#10b981" radius={[4, 4, 0, 0]} name="Km" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Top pilots + drones */}
          <div className="grid md:grid-cols-2 gap-3">
            <Card className="card-hud p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2">
                <Trophy size={12} /> TOP 3 PILOTOS
              </p>
              {report.top_pilots?.length > 0 ? (
                <ul className="space-y-2" data-testid="wr-top-pilots">
                  {report.top_pilots.map((p, i) => (
                    <li key={i} className="flex justify-between items-center text-sm border-b border-gray-100 pb-1.5">
                      <span className="flex items-center gap-2 text-navy">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i===0?'bg-gold text-navy':i===1?'bg-gray-300 text-gray-700':'bg-amber-200 text-amber-800'}`}>
                          {i+1}
                        </span>
                        {p.name}
                      </span>
                      <span className="font-mono text-navy">{p.flights} voos</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-gray-400 text-xs">Sem pilotos no periodo</p>}
            </Card>

            <Card className="card-hud p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2">
                <Plane size={12} /> DRONES MAIS USADOS
              </p>
              {report.top_drones?.length > 0 ? (
                <ul className="space-y-2" data-testid="wr-top-drones">
                  {report.top_drones.map((d, i) => (
                    <li key={i} className="flex justify-between items-center text-sm border-b border-gray-100 pb-1.5">
                      <span className="text-navy">{d.model}</span>
                      <span className="font-mono text-navy">{d.flights} voos</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-gray-400 text-xs">Sem drones no periodo</p>}
            </Card>
          </div>

          {/* Records */}
          <Card className="card-hud p-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gold mb-3 flex items-center gap-2">
              <Gauge size={12} /> RECORDES
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-gray-500 block text-xs">Altitude max</span><span className="font-mono text-navy">{rec.max_altitude_m || 0} m</span></div>
              <div><span className="text-gray-500 block text-xs">Velocidade max</span><span className="font-mono text-navy">{rec.max_speed_ms || 0} m/s</span></div>
              <div><span className="text-gray-500 block text-xs">Voo mais longo</span><span className="font-mono text-navy">{Math.floor((rec.longest_flight_s || 0) / 60)}min</span></div>
              <div><span className="text-gray-500 block text-xs">Maior distancia</span><span className="font-mono text-navy">{((rec.longest_distance_m || 0) / 1000).toFixed(2)} km</span></div>
            </div>
          </Card>

          {/* Low battery events */}
          {report.low_battery_events?.length > 0 && (
            <Card className="card-hud p-4 border-amber-200">
              <p className="text-[10px] font-mono uppercase tracking-widest text-amber-600 mb-3 flex items-center gap-2">
                <AlertTriangle size={12} /> ALERTAS DE BATERIA ({report.low_battery_events.length})
              </p>
              <ul className="space-y-2" data-testid="wr-low-battery">
                {report.low_battery_events.map((e, i) => (
                  <li key={i} className="flex justify-between items-center text-xs border-b border-amber-100 pb-1.5">
                    <span className="text-navy">
                      <Battery size={11} className="inline text-amber-600 mr-1" />
                      {e.pilot_name || '-'} · {e.drone_model || '-'}
                    </span>
                    <span className="font-mono text-amber-600 font-semibold">{e.battery_end_pct}%</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="card-hud p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-lg ${color}`}><Icon size={14} /></div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-navy font-mono">{value}</p>
    </Card>
  );
}
